import { match } from 'ts-pattern'

import type {
  ResolutionContext,
  SimulationExpression
} from '../../simulation.types'
import { parseTaskOutputReference } from '../parseTaskOutputReference'
import { readDataFromTask } from '../readDataFromTask'

/**
 * Turns the switch's input parameters into the `$` object its expression reads,
 * swapping each reference for the value that came back from the task producing
 * it.
 *
 * The keys go across exactly as they are, scoped forms like
 * 'sequence.quote.attachments::tpl-a' included, because the compiled expression
 * asks for them by their full name.
 */
export const buildExpressionScope = ({
  expression,
  context
}: {
  expression: SimulationExpression
  context: ResolutionContext
}): Record<string, unknown> =>
  Object.entries(expression.switchInputParameters).reduce<
    Record<string, unknown>
  >(
    (scope, [key, reference]) => ({
      ...scope,
      [key]: resolveSwitchReference({ reference, context })
    }),
    {}
  )

/**
 * Swaps one of a switch's references for the value behind it - something a task
 * gave us, something the workflow was started with, or a plain value written
 * into the definition.
 *
 * A reference we can't fill in reads as undefined rather than stopping the run.
 * The conditions are written to cope with a missing value, and a task we can't
 * fetch has already been noted on the route it belongs to.
 */
const resolveSwitchReference = ({
  reference,
  context
}: {
  reference: string
  context: ResolutionContext
}): unknown =>
  match(parseTaskOutputReference(reference))
    .with({ kind: 'task' }, ({ taskReferenceName, path }) =>
      readDataFromTask({
        source: context.taskOutputs[taskReferenceName],
        path
      })
    )
    .with(
      { kind: 'workflowInput' },
      ({ field }) => context.workflowInput[field]
    )
    .with({ kind: 'literal' }, ({ value }) => value)
    .exhaustive()
